<template>
  <div>
    <div
      v-if="isRecordingUI"
      class="recording-overlay"
      :class="{ cancel: isCancelReady }"
    >
      <div class="recording-title">我在听，请说话</div>
      <img class="recording-wave" :src="waveImg" alt="voice wave" />
      <div class="recording-tip">
        {{ isCancelReady ? "松开取消" : "松开发送，上滑取消" }}
      </div>
    </div>

    <div class="footer">
      <div class="input-wrapper" :class="{ 'voice-mode': isVoiceMode }">
        <div
          v-if="voiceEnabled"
          class="toggle-btn"
          @click="toggleInputMode"
          :aria-label="isVoiceMode ? '切换文字输入' : '切换语音输入'"
        >
          <!-- 语音模式：显示键盘图标 -->
          <svg
            v-if="isVoiceMode"
            class="icon"
            viewBox="0 0 24 24"
            fill="currentColor"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M20 5H4c-1.1 0-1.99.9-1.99 2L2 17c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm-9 3h2v2h-2V8zm0 3h2v2h-2v-2zM8 8h2v2H8V8zm0 3h2v2H8v-2zm-1 2H5v-2h2v2zm0-3H5V8h2v2zm9 7H8v-2h8v2zm0-4h-2v-2h2v2zm0-3h-2V8h2v2zm3 3h-2v-2h2v2zm0-3h-2V8h2v2z"
            />
          </svg>
          <!-- 文字模式：显示麦克风图标 -->
          <svg
            v-else
            class="icon"
            viewBox="0 0 24 24"
            fill="currentColor"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"
            />
            <path
              d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"
            />
          </svg>
        </div>

        <template v-if="voiceEnabled && isVoiceMode">
          <button
            class="record-btn"
            type="button"
            @mousedown.prevent="handleRecordStart"
            @mouseup.prevent="handleRecordEnd"
            @mouseleave.prevent="handleRecordCancel"
            @mousemove.prevent="handleRecordMove"
            @touchstart.prevent="handleRecordStart"
            @touchend.prevent="handleRecordEnd"
            @touchmove.prevent="handleRecordMove"
          >
            按住说话
          </button>
        </template>
        <template v-else>
          <input
            v-model="inputText"
            @keyup.enter="handleSend"
            placeholder="请输入"
          />
        </template>
      </div>
      <button v-if="!isVoiceMode" @click="handleSend">发送</button>
    </div>
  </div>
</template>

<script>

export default {
  name: "VoiceInput",
  props: {
    // 是否打开语音输入功能，默认打开
    voiceEnabled: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      isVoiceMode: false,
      recordingData: null,
      mediaRecorder: null,
      mediaStream: null,
      recordingError: "",
      isRecordingUI: false,
      isCancelReady: false,
      touchStartY: 0,
      waveImg: require("@/assets/voice-wave.gif"),
      inputText: "",
    };
  },
  methods: {
    /**
     * 切换输入模式（文字/语音）
     */
    toggleInputMode() {
      this.isVoiceMode = !this.isVoiceMode;
    },

    getClientY(event) {
      if (event.touches && event.touches.length) {
        return event.touches[0].clientY;
      }
      return event.clientY;
    },

    handleRecordStart(event) {
      this.isCancelReady = false;
      this.touchStartY = this.getClientY(event) || 0;
      this.isRecordingUI = true;
      this.addRecordListeners();
      this.startRecording();
    },

    handleRecordMove(event) {
      if (!this.isRecordingUI) return;
      const currentY = this.getClientY(event);
      if (!currentY) return;
      // 上滑超过 50px 进入可取消态
      this.isCancelReady = this.touchStartY - currentY > 50;
    },

    handleRecordEnd() {
      if (!this.isRecordingUI) return;
      if (this.isCancelReady) {
        this.cancelRecording();
      } else {
        this.stopRecording();
      }
      this.removeRecordListeners();
      this.isRecordingUI = false;
      this.isCancelReady = false;
    },

    handleRecordCancel() {
      if (!this.isRecordingUI) return;
      this.cancelRecording();
      this.removeRecordListeners();
      this.isRecordingUI = false;
      this.isCancelReady = false;
    },

    addRecordListeners() {
      window.addEventListener("touchmove", this.handleRecordMove, {
        passive: false,
      });
      window.addEventListener("touchend", this.handleRecordEnd, {
        passive: false,
      });
      window.addEventListener("touchcancel", this.handleRecordCancel, {
        passive: false,
      });
      window.addEventListener("mousemove", this.handleRecordMove);
      window.addEventListener("mouseup", this.handleRecordEnd);
      window.addEventListener("mouseleave", this.handleRecordCancel);
    },

    removeRecordListeners() {
      window.removeEventListener("touchmove", this.handleRecordMove);
      window.removeEventListener("touchend", this.handleRecordEnd);
      window.removeEventListener("touchcancel", this.handleRecordCancel);
      window.removeEventListener("mousemove", this.handleRecordMove);
      window.removeEventListener("mouseup", this.handleRecordEnd);
      window.removeEventListener("mouseleave", this.handleRecordCancel);
    },

    // 获取用户媒体流（兼容移动端）
    async getUserMedia() {
      console.log("检测录音 API 支持情况:", {
        hasMediaDevices: !!navigator.mediaDevices,
        hasGetUserMedia: !!(
          navigator.mediaDevices && navigator.mediaDevices.getUserMedia
        ),
        hasLegacyGetUserMedia: !!navigator.getUserMedia,
        hasWebkitGetUserMedia: !!navigator.webkitGetUserMedia,
        hasMozGetUserMedia: !!navigator.mozGetUserMedia,
        userAgent: navigator.userAgent,
        isSecureContext: window.isSecureContext,
        protocol: window.location.protocol,
      });

      // 优先使用新 API
      if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
        console.log("使用 navigator.mediaDevices.getUserMedia");
        return navigator.mediaDevices.getUserMedia({ audio: true });
      }
      // 兼容旧版 API（部分移动端浏览器）
      else if (navigator.getUserMedia) {
        console.log("使用 navigator.getUserMedia (旧版 API)");
        return new Promise((resolve, reject) => {
          navigator.getUserMedia({ audio: true }, resolve, reject);
        });
      }
      // 兼容 webkit 前缀（iOS Safari 等）
      else if (navigator.webkitGetUserMedia) {
        console.log("使用 navigator.webkitGetUserMedia");
        return new Promise((resolve, reject) => {
          navigator.webkitGetUserMedia({ audio: true }, resolve, reject);
        });
      }
      // 兼容 moz 前缀（Firefox）
      else if (navigator.mozGetUserMedia) {
        console.log("使用 navigator.mozGetUserMedia");
        return new Promise((resolve, reject) => {
          navigator.mozGetUserMedia({ audio: true }, resolve, reject);
        });
      } else {
        const errorMsg =
          "当前浏览器不支持录音功能。请使用 Chrome、Safari、Edge 等现代浏览器，并确保使用 HTTPS 协议访问。";
        console.error("未找到任何 getUserMedia API", {
          userAgent: navigator.userAgent,
          protocol: window.location.protocol,
        });
        throw new Error(errorMsg);
      }
    },

    /**
     * 获取浏览器支持的第一个音频格式
     * @returns {string} 返回支持的 MIME 类型
     */
    getFirstSupportedMimeType() {
      const formats = [
        "audio/webm;codecs=opus",
        "audio/webm",
        "audio/mp4",
        "audio/ogg",
        "audio/wav"
      ];
      
      for (const format of formats) {
        if (MediaRecorder.isTypeSupported(format)) {
          return format;
        }
      }
      
      return "";
    },

    async startRecording() {
      if (this.mediaRecorder && this.mediaRecorder.state === "recording")
        return;

      // 先检查 MediaRecorder 是否支持
      if (typeof MediaRecorder === "undefined") {
        this.recordingError =
          "当前浏览器不支持 MediaRecorder API，请使用 Chrome、Safari 等现代浏览器";
        console.error("MediaRecorder 不支持");
        alert(this.recordingError);
        this.isRecordingUI = false;
        this.removeRecordListeners();
        return;
      }

      try {
        // 获取媒体流（兼容移动端）
        const stream = await this.getUserMedia();
        this.mediaStream = stream;
        const chunks = [];

        // 尝试获取浏览器支持的音频格式
        const mimeType = this.getFirstSupportedMimeType();
        const options = mimeType ? { mimeType } : {};

        console.log("尝试创建 MediaRecorder", {
          mimeType: mimeType || "默认",
          userAgent: navigator.userAgent,
        });

        // 创建 MediaRecorder
        let recorder;
        try {
          recorder = new MediaRecorder(stream, options);
        } catch (recorderError) {
          console.warn("使用指定格式创建失败，尝试使用默认格式", recorderError);
          recorder = new MediaRecorder(stream);
        }

        this.mediaRecorder = recorder;
        this.recordingError = "";
        this.recordingData = {
          startTime: Date.now(),
          chunks,
          mimeType: recorder.mimeType || mimeType || "audio/webm",
        };

        // 设置数据收集间隔（移动端建议设置）
        const timeslice = 1000; // 每1秒收集一次数据

        recorder.ondataavailable = (event) => {
          if (event.data && event.data.size > 0) {
            chunks.push(event.data);
          }
        };

        recorder.onstop = () => {
          // 若已被取消或状态已清理，直接退出
          if (!this.recordingData || !this.recordingData.startTime) {
            this.cleanupMedia();
            return;
          }

          const endTime = Date.now();
          // 使用记录的 mimeType，如果没有则尝试从 recorder 获取
          const finalMimeType =
            this.recordingData.mimeType || recorder.mimeType || "audio/webm";
          const blob = new Blob(chunks, { type: finalMimeType });
          const url = URL.createObjectURL(blob);
          this.recordingData = {
            ...this.recordingData,
            endTime,
            blob,
            url,
            duration: endTime - this.recordingData.startTime,
          };
          this.cleanupMedia();
          console.log("录音完成（待转换为 MP3）", this.recordingData);
          // 发送录音数据给父组件
          this.$emit("record-complete", this.recordingData);
        };

        // 移动端建议使用 timeslice 参数
        recorder.start(timeslice);
        console.log("开始录音", { mimeType: mimeType || "默认格式" });
      } catch (err) {
        let errorMessage = "录音失败";

        console.error("录音失败详情:", {
          error: err,
          name: err.name,
          message: err.message,
          userAgent: navigator.userAgent,
          hasMediaDevices: !!navigator.mediaDevices,
          hasGetUserMedia: !!(
            navigator.mediaDevices && navigator.mediaDevices.getUserMedia
          ),
          hasMediaRecorder: typeof MediaRecorder !== "undefined",
          isSecureContext: window.isSecureContext,
          protocol: window.location.protocol,
        });

        if (
          err.name === "NotAllowedError" ||
          err.name === "PermissionDeniedError"
        ) {
          errorMessage = "需要麦克风权限，请在浏览器设置中允许访问麦克风";
        } else if (
          err.name === "NotFoundError" ||
          err.name === "DevicesNotFoundError"
        ) {
          errorMessage = "未检测到麦克风设备";
        } else if (
          err.name === "NotSupportedError" ||
          err.name === "TypeError"
        ) {
          errorMessage =
            "当前浏览器不支持录音功能，请使用 Chrome、Safari 等现代浏览器";
        } else if (err.message) {
          errorMessage = err.message;
        }

        // 检查是否是 HTTPS 问题
        if (
          window.location.protocol !== "https:" &&
          window.location.hostname !== "localhost"
        ) {
          errorMessage += "\n\n提示：某些浏览器要求使用 HTTPS 才能访问麦克风";
        }

        this.recordingError = errorMessage;
        alert(errorMessage);
        this.isRecordingUI = false;
        this.removeRecordListeners();
      }
    },

    stopRecording() {
      if (this.mediaRecorder && this.mediaRecorder.state === "recording") {
        this.mediaRecorder.stop();
      } else {
        this.cleanupMedia();
      }
    },

    cancelRecording() {
      if (this.mediaRecorder && this.mediaRecorder.state === "recording") {
        this.mediaRecorder.stop();
      }
      this.cleanupMedia();
      this.recordingData = null;
      console.log("录音已取消");
    },

    cleanupMedia() {
      if (this.mediaStream) {
        this.mediaStream.getTracks().forEach((t) => t.stop());
      }
      this.mediaStream = null;
      this.mediaRecorder = null;
    },

    handleSend() {
      if (this.inputText.trim()) {
        this.$emit("send", this.inputText);
        this.inputText = "";
      }
    },
  },
  beforeDestroy() {
    // 组件销毁前清理事件监听和媒体资源
    this.removeRecordListeners();
    this.cleanupMedia();
  },
};
</script>

<style lang="scss" scoped>
.footer {
  width: 100%;
  position: absolute;
  bottom: 0;
  display: flex;
  align-items: center;
  box-sizing: border-box;
  padding: 0 3%;
  padding-bottom: 24px;

  .input-wrapper {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 4px;
    padding-left: 8px;
    background: rgba(255, 255, 255, 0.6);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    border-radius: 25px;
    margin-right: 2%;
    border: 1px solid rgba(255, 255, 255, 0.3);
    box-shadow: inset 0 1px 2px rgba(255, 255, 255, 0.5),
      0 2px 8px rgba(0, 0, 0, 0.05);
  }

  .toggle-btn {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    border: none;
    background: white;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    flex-shrink: 0;
    transition: all 0.2s ease;

    &:hover {
      background: #f5f5f5;
    }

    &:active {
      transform: scale(0.95);
    }

    .icon {
      width: 24px;
      height: 24px;
      color: #666;
    }
  }

  input {
    flex: 1;
    padding: 8px 12px;
    background-color: transparent;
    border-radius: 20px;
    outline: none;
    border: none;
    width: 100%;
    color: #666666;
    height: 30px;
  }

  .record-btn {
    flex: 1;
    height: 46px;
    border-radius: 20px;
    border: none;
    background: linear-gradient(90deg, #9cc3ff, #b5a8ff);
    color: white;
    font-size: 14px;
    cursor: pointer;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.08);
  }

  button {
    min-width: 64px;
    height: 40px;
    border: none;
    border-radius: 20px;
    font-size: 15px;
    font-weight: 500;
    color: #fff;
    padding: 0 20px;
    background: linear-gradient(135deg, #6e8efb, #a777e3);
    box-shadow: 0 4px 12px rgba(110, 142, 251, 0.3),
      inset 0 1px 0 rgba(255, 255, 255, 0.2);
    cursor: pointer;
    transition: all 0.3s ease;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 6px 16px rgba(110, 142, 251, 0.4),
        inset 0 1px 0 rgba(255, 255, 255, 0.3);
    }

    &:active {
      transform: translateY(0);
      box-shadow: 0 2px 8px rgba(110, 142, 251, 0.3),
        inset 0 1px 0 rgba(255, 255, 255, 0.2);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      transform: none;
    }
  }
}

.recording-overlay {
  position: absolute;
  left: 8%;
  right: 8%;
  bottom: 90px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  pointer-events: none;
  z-index: 5;
  color: #333;
  background-color: #fff;
  border-radius: 32px;
  width: 84%;
  padding: 48px 0 54px;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.08);
}

.recording-overlay .recording-title {
  font-size: 18px;
  font-weight: 700;
}

.recording-overlay .recording-wave {
  width: 280px;
  height: 36px;
  object-fit: contain;
}

.recording-overlay .recording-tip {
  font-size: 14px;
  color: #a3a3a3;
}

.recording-overlay.cancel .recording-tip {
  color: #ff4d4f;
}
</style>

